import { useRef } from 'react';
import { AlertCircle, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface DueDatePickerProps {
  dueDate?: string | null;
  onChange?: (dueDate: string | null) => void;
  isOverdue?: boolean;
  isCompleted?: boolean;
  stopDragPointerDown?: boolean;
}

export function DueDatePicker({
  dueDate,
  onChange,
  isOverdue,
  isCompleted,
  stopDragPointerDown = true,
}: DueDatePickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const editable = !!onChange && !isCompleted;

  const formatted = dueDate
    ? new Date(dueDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      })
    : null;

  const textColor = isCompleted
    ? 'text-grey-400'
    : dueDate
      ? isOverdue
        ? 'text-error'
        : 'text-grey-500'
      : 'text-grey-300';

  if (!editable) {
    if (!formatted) return null;
    return (
      <span className={cn('flex items-center gap-1 flex-shrink-0', textColor)}>
        {isOverdue ? <AlertCircle className="w-3 h-3" /> : <Calendar className="w-3 h-3" />}
        <span>{formatted}</span>
      </span>
    );
  }

  return (
    <div className="relative flex-shrink-0">
      <button
        type="button"
        onPointerDown={stopDragPointerDown ? (e) => e.stopPropagation() : undefined}
        onClick={(e) => {
          e.stopPropagation();
          inputRef.current?.showPicker?.();
        }}
        className={cn(
          'flex items-center gap-1 rounded px-1.5 py-0.5 transition-colors hover:bg-grey-100',
          textColor,
        )}
        title={dueDate ? 'Change due date' : 'Set due date'}
      >
        {isOverdue && dueDate ? (
          <AlertCircle className="w-3 h-3" />
        ) : (
          <Calendar className="w-3 h-3" />
        )}
        {formatted && <span>{formatted}</span>}
      </button>
      <input
        ref={inputRef}
        type="date"
        className="absolute inset-0 opacity-0 pointer-events-none"
        value={dueDate ? new Date(dueDate).toISOString().split('T')[0] : ''}
        onChange={(e) => {
          e.stopPropagation();
          onChange(e.target.value || null);
        }}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        tabIndex={-1}
      />
    </div>
  );
}
